import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getArrayLength } from 'utils';
import { PublicListingRequirementKey } from 'router/subrouters/ListingCreationWizard/data/publicListingRequirements';
import usePublicListingRequirements from 'router/subrouters/ListingCreationWizard/hooks/usePublicListingRequirements';
import PreviewMissingFieldsContext, {
  PreviewFocusRequest,
} from './PreviewMissingFields.context';

type PreviewMissingFieldsProps = {
  isUrgent: boolean;
};

/**
 * Resolves the shared rule set against the live form values and holds which
 * requirements are currently shown as outstanding.
 *
 * Mounted inside the preview form - it reads form state - and scoped to the
 * preview page, so leaving preview mode starts from a clean draft rather than a
 * set of errors.
 */
const PreviewMissingFields: React.FC<PreviewMissingFieldsProps> = (props) => {
  const { children, isUrgent } = props;

  const {
    requirements,
    missingKeys,
    missingCount,
    enteredCount,
    isRequired,
    isSatisfied,
  } = usePublicListingRequirements(isUrgent);

  const [markedKeys, setMarkedKeys] = useState<PublicListingRequirementKey[]>(
    [],
  );
  const [focusRequest, setFocusRequest] = useState<PreviewFocusRequest | null>(
    null,
  );

  /**
   * Satisfying a requirement clears its own mark and leaves every other mark
   * standing. Driven from the rule set rather than from the field, so the
   * two-of-three group clears once the rule is met from anywhere.
   */
  useEffect(() => {
    setMarkedKeys((prev) => {
      const next = prev.filter((key) => missingKeys.includes(key));

      return getArrayLength(next) === getArrayLength(prev) ? prev : next;
    });
  }, [missingKeys]);

  const markIfMissing = useCallback(
    (key: PublicListingRequirementKey) => {
      if (!missingKeys.includes(key)) return;

      setMarkedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    },
    [missingKeys],
  );

  const markAllMissing = useCallback(
    () => setMarkedKeys(missingKeys),
    [missingKeys],
  );

  const isMarked = useCallback(
    (key: PublicListingRequirementKey) => markedKeys.includes(key),
    [markedKeys],
  );

  const requestFocus = useCallback(
    (fieldName: string, focusFieldName?: string) =>
      setFocusRequest({ fieldName, focusFieldName }),
    [],
  );

  const consumeFocusRequest = useCallback(() => setFocusRequest(null), []);

  const value = useMemo(
    () => ({
      requirements,
      missingKeys,
      missingCount,
      enteredCount,
      isRequired,
      isSatisfied,
      isMarked,
      markIfMissing,
      markAllMissing,
      focusRequest,
      requestFocus,
      consumeFocusRequest,
    }),
    [
      requirements,
      missingKeys,
      missingCount,
      enteredCount,
      isRequired,
      isSatisfied,
      isMarked,
      markIfMissing,
      markAllMissing,
      focusRequest,
      requestFocus,
      consumeFocusRequest,
    ],
  );

  return (
    <PreviewMissingFieldsContext.Provider value={value}>
      {children}
    </PreviewMissingFieldsContext.Provider>
  );
};

export default PreviewMissingFields;
